import {
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  LoaderCircle,
  MessageSquareText,
  Pause,
  Play,
  Send,
  ShieldCheck,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import type {
  ProductionReviewCandidate,
  ProductionReviewTask,
  ScratchCorrectionEdit,
  ScratchCorrectionKind,
} from "../../shared/types";
import { api, uploadScratchCorrectionAsset } from "../api";
import { WhiteboardPackageSurface } from "./WhiteboardPackageSurface";
import { WhiteboardFinalSurface } from "./WhiteboardFinalSurface";

export type PendingScratchEdit = ScratchCorrectionEdit & {
  targetTitle: string;
  resourceLabel?: string;
  replacementFile?: File | null;
};

export function upsertPendingScratchEdits(current: PendingScratchEdit[], additions: PendingScratchEdit[]) {
  const next = [...current];
  for (const addition of additions) {
    const index = next.findIndex((item) => item.kind === addition.kind && item.target.component_id === addition.target.component_id);
    if (index >= 0) next[index] = { ...addition, edit_id: next[index]!.edit_id };
    else next.push(addition);
  }
  return next;
}

export function scratchCorrectionLabel(kind: ScratchCorrectionKind) {
  const labels: Record<ScratchCorrectionKind, string> = {
    script_patch: "Script",
    storyboard_patch: "Storyboard",
    character_profile_patch: "Character profile",
    character_sheet_regeneration: "Character sheet",
    character_reclassification: "Not a character — material/prop",
    style_patch: "Style",
    staging_image_regeneration: "Staging image",
    video_regeneration: "Video",
    literal_image_replacement: "Uploaded image",
    literal_video_replacement: "Uploaded video",
    voice_selection: "Voice",
    audio_replacement: "Uploaded audio",
    caption_only: "Captions",
    assembly_only: "Assembly",
    model_override: "Video model",
  };
  return labels[kind];
}

export function scratchVideoModelOverride(model: string, modelOnly: boolean): ScratchCorrectionEdit["model_override"] {
  return model.trim() ? { requested_model: model.trim(), fallback_policy: modelOnly ? "model_only" : "approved_router_secondary" } : null;
}

export function scratchPendingFeedbackMessage(edits: PendingScratchEdit[]) {
  const lines = edits.map((edit, index) => `${index + 1}. ${scratchCorrectionLabel(edit.kind)} · ${edit.targetTitle}: ${edit.instruction}`);
  return [`Requested ${edits.length} targeted change${edits.length === 1 ? "" : "s"}:`, ...lines].join("\n");
}

export const scratchProvisionalDecisionCopy = {
  proposedLabel: "Newest proposed set",
  currentLabel: "Current review set",
  acceptLabel: "Use entire newest set",
  discardLabel: "Keep current review set",
  reviseHeading: "Revise the newest set",
};

export function scratchProvisionalOutcome(candidate: Pick<ProductionReviewCandidate, "content"> | null) {
  const findings = arrayValue(candidate?.content.provisionalObjectiveFindings).map(objectValue);
  const missed = findings.filter((finding) => arrayValue(finding.violation_codes).includes("targeted_correction_not_applied"));
  if (missed.length) return {
    title: "QA reports that your requested correction failed",
    detail: "The requested change was not detected in the replacement. Compare it with the original before deciding. Your current review set is unchanged.",
    acceptLabel: "Use newest set despite failed correction",
  };
  if (findings.length) return {
    title: "Correction needs review — unresolved QA findings",
    detail: "The replacement has unresolved or uncertain quality findings. Generation completed, but that does not prove the correction worked. Your current review set is unchanged.",
    acceptLabel: "Use newest set with QA warnings",
  };
  return {
    title: "Newest proposed clips ready for your review",
    detail: "Your current review set is still unchanged. Earlier discarded proposals remain in history and are not part of either choice below.",
    acceptLabel: scratchProvisionalDecisionCopy.acceptLabel,
  };
}

export function scratchApprovalBlockReason(
  gate: string | undefined,
  pendingEditCount: number,
  draftMessage: string,
) {
  if (!gate?.startsWith("scratch_")) return null;
  if (pendingEditCount > 0) {
    return `Submit or remove ${pendingEditCount} pending change${pendingEditCount === 1 ? "" : "s"} before approving this output.`;
  }
  const trimmedMessage = draftMessage.trim();
  if (trimmedMessage && !/^(approve|approved)$/iu.test(trimmedMessage)) {
    return "Add or clear the draft change before approving this output.";
  }
  return null;
}

export function approvalProgressCopy(status: ProductionReviewTask["status"]) {
  if (status !== "action_queued") return null;
  return {
    buttonLabel: "Approval queued",
    title: "Approval accepted",
    detail: "This exact revision is locked. The runner is advancing the workflow, and this page will update automatically when the next review is ready.",
  };
}

export function approvalProgressCopyForTask(task: Pick<ProductionReviewTask, "status" | "runnerEligibility">) {
  const base = approvalProgressCopy(task.status);
  if (!base || task.runnerEligibility?.state !== "missing") return base;
  return {
    ...base,
    title: "Approval accepted · waiting for a compatible runner",
    detail: "This exact revision is locked. No compatible runner is currently registered for this Scratch job, so no provider work has started. Recovery is automatic when the exact runner comes online.",
  };
}

export function correctionProgressCopy(task: Pick<ProductionReviewTask, "status" | "runnerEligibility">) {
  if (task.status !== "correction_processing") return null;
  const eligibility = task.runnerEligibility;
  if (eligibility?.jobStatus === "leased") {
    return {
      title: "Targeted correction in progress",
      detail: "A runner is actively processing this correction. The current version stays playable until the replacement validates and is committed.",
    };
  }
  if (eligibility?.state === "missing") {
    return {
      title: "Correction queued · waiting for a compatible runner",
      detail: "Your correction is safely queued, but no compatible runner is currently online. No provider work or spending has started.",
    };
  }
  if (eligibility?.state === "busy") {
    return {
      title: "Correction queued · compatible runner busy",
      detail: "Your correction is safely queued and will start when the compatible runner finishes its current job. No provider work has started for this correction.",
    };
  }
  return {
    title: "Correction queued",
    detail: "Your correction is safely queued and a compatible runner is available to claim it. The current version remains playable until processing starts.",
  };
}

function scratchCorrectionInvokesProvider(kind: ScratchCorrectionKind) {
  return ["character_profile_patch", "character_sheet_regeneration", "style_patch", "staging_image_regeneration", "video_regeneration"].includes(kind);
}

function scratchCorrectionOutputMode(kind: ScratchCorrectionKind): ScratchCorrectionEdit["output_mode"] {
  if (kind === "character_reclassification") return "patch";
  if (kind.endsWith("_patch")) return "patch";
  if (kind.includes("replacement")) return "replace";
  if (scratchCorrectionInvokesProvider(kind)) return "regenerate";
  return "recompile";
}

export function ProductionReviewPage({
  actionsEnabled = true,
}: {
  actionsEnabled?: boolean;
}) {
  const { id = "" } = useParams();
  const navigate = useNavigate();
  const [task, setTask] = useState<ProductionReviewTask | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [focus, setFocus] = useState(0);
  const [selectedStaticCandidateStepIds, setSelectedStaticCandidateStepIds] =
    useState<string[]>([]);
  const [correctionKind, setCorrectionKind] =
    useState<ScratchCorrectionKind>("staging_image_regeneration");
  const [selectedScratchStepIds, setSelectedScratchStepIds] = useState<string[]>([]);
  const [videoModel, setVideoModel] = useState("");
  const [modelOnly, setModelOnly] = useState(false);
  const [imageCorrectionMode, setImageCorrectionMode] = useState<"patch" | "regenerate">("patch");
  const [resourceOverrideId, setResourceOverrideId] = useState("");
  const [replacementFile, setReplacementFile] = useState<File | null>(null);
  const [pendingScratchEdits, setPendingScratchEdits] = useState<PendingScratchEdit[]>([]);
  const [composerTargetStepIds, setComposerTargetStepIds] = useState<string[]>([]);
  const [showProvisionalCorrection, setShowProvisionalCorrection] = useState(true);
  const activeInteractionMs = useRef(0);
  const lastInteractionTick = useRef(performance.now());
  const lastActivityAt = useRef(Date.now());
  const load = async () => {
    try {
      const value = await api.productionReview(id);
      setTask(value);
      setError("");
      if (value.status === "resolved" && !["scratch-whiteboard-package-v1", "scratch-whiteboard-final-v1"].includes(String(value.currentCandidate?.content.schemaVersion)))
        window.setTimeout(
          () =>
            navigate(
              value.nextTaskId ? `/kanban/${value.nextTaskId}` : "/kanban",
            ),
          900,
        );
    } catch (reason) {
      setError(
        reason instanceof Error
          ? reason.message
          : "Could not load this review.",
      );
    }
  };
  const whiteboardTerminal = task?.whiteboardChain?.status === 'paused' || task?.whiteboardChain?.status === 'checked_pending_assembly';
  useEffect(() => {
    void load();
    if (whiteboardTerminal) return;
    const timer = window.setInterval(() => void load(), 5000);
    return () => window.clearInterval(timer);
  }, [id, whiteboardTerminal]);
  const interactionEnabled = Boolean(task?.gate.startsWith("scratch_") && task.status === "open");
  const readActiveInteractionMs = () => {
    const now = performance.now();
    if (interactionEnabled && document.visibilityState === "visible" && Date.now() - lastActivityAt.current <= 30_000)
      activeInteractionMs.current += Math.max(0, now - lastInteractionTick.current);
    lastInteractionTick.current = now;
    return Math.round(activeInteractionMs.current);
  };
  const resetActiveInteraction = () => {
    activeInteractionMs.current = 0;
    lastInteractionTick.current = performance.now();
    lastActivityAt.current = Date.now();
  };
  useEffect(() => {
    resetActiveInteraction();
  }, [id]);
  useEffect(() => {
    if (!interactionEnabled) {
      lastInteractionTick.current = performance.now();
      return;
    }
    const markActivity = () => {
      readActiveInteractionMs();
      lastActivityAt.current = Date.now();
    };
    const timer = window.setInterval(() => void readActiveInteractionMs(), 1000);
    window.addEventListener("pointerdown", markActivity, { passive: true });
    window.addEventListener("keydown", markActivity);
    window.addEventListener("scroll", markActivity, { passive: true });
    document.addEventListener("visibilitychange", markActivity);
    return () => {
      readActiveInteractionMs();
      window.clearInterval(timer);
      window.removeEventListener("pointerdown", markActivity);
      window.removeEventListener("keydown", markActivity);
      window.removeEventListener("scroll", markActivity);
      document.removeEventListener("visibilitychange", markActivity);
    };
  }, [interactionEnabled]);
  const provisionalCorrection = task?.provisionalCorrection ?? null;
  const provisionalOutcome = scratchProvisionalOutcome(provisionalCorrection?.candidate ?? null);
  const candidate = provisionalCorrection && showProvisionalCorrection
    ? provisionalCorrection.candidate
    : task?.currentCandidate ?? null;
  const rawSteps = useMemo(() => candidateSteps(candidate), [candidate]);
  const steps = useMemo(
    () => reviewSteps(candidate, task?.gate),
    [candidate, task?.gate],
  );
  const current = steps[Math.min(focus, Math.max(0, steps.length - 1))] ?? null;
  const staticPlan = staticPlanReview(current?.content);
  const reviewable =
    task?.gate !== "static_creative_plan" || staticPlan !== null;
  const staticCandidateReview = task?.gate === "static_candidate_batch_review";
  const staticMembers = useMemo(
    () => [
      ...new Set(
        steps
          .map((step) => step.stepId.split("-candidate-")[0])
          .filter(Boolean),
      ),
    ],
    [steps],
  );
  const selectedStaticMembers = useMemo(
    () =>
      new Set(
        selectedStaticCandidateStepIds.map(
          (stepId) => stepId.split("-candidate-")[0],
        ),
      ).size,
    [selectedStaticCandidateStepIds],
  );
  const staticSelectionReady =
    !staticCandidateReview ||
    (selectedStaticCandidateStepIds.length === staticMembers.length &&
      selectedStaticMembers === staticMembers.length);
  const scratchCorrection = Boolean(task?.gate.startsWith("scratch_"));
  const nativeDialogue = Boolean(candidate?.content.nativeDialogue);
  const nativeSpeakerDirections = nativeDialogue && Boolean(current?.stepId.startsWith("voice-"));
  // A scripted sound effect or music bed is not a voice: it must never be
  // described as, or corrected through, the voice-changer route.
  const scriptedCue = Boolean(stringValue(current?.content.cueId));
  const invokesProvider = task?.gate.startsWith("scratch_")
    ? scratchCorrectionInvokesProvider(correctionKind)
    : current?.contentKind === "image" ||
      current?.contentKind === "video" ||
      current?.contentKind === "audio";
  const affectedItemCount = Math.max(
    1,
    Number(current?.content.affectedItemCount ?? 1),
  );
  const approvalMessage = /^(approve|approved)$/iu.test(message.trim());
  const approvalBlockReason = scratchApprovalBlockReason(
    task?.gate,
    pendingScratchEdits.length,
    message,
  );
  const approvalProgress = task ? approvalProgressCopyForTask(task) : null;
  const correctionProgress = task ? correctionProgressCopy(task) : null;
  const changedItems = useMemo(() => arrayValue(candidate?.content.changedItems).map(objectValue), [candidate]);
  const automaticRepairs = useMemo(() => arrayValue(candidate?.content.automaticRepairs).map(objectValue), [candidate]);
  const currentObjectiveFindings = useMemo(
    () => arrayValue(current?.content.semanticQaFindings)
      .map(objectValue)
      .filter((finding) => stringValue(finding.classification) === "objective"),
    [current],
  );
  const currentTargetIds = current?.targetStepIds?.length
    ? current.targetStepIds
    : current
      ? [current.stepId]
      : [];
  const currentChanged = Boolean(
    currentTargetIds.length &&
      changedItems.some((item) =>
        currentTargetIds.includes(stringValue(item.componentId)),
      ),
  );
  const pendingVoiceEdit = pendingScratchEdits.find((edit) => edit.kind === "voice_selection" && edit.target.component_id === current?.stepId);
  const selectedVoiceId = pendingVoiceEdit?.resource_override_id ?? (current?.stepId.startsWith('voice-native-') ? '' : resourceOverrideId);
  const composerTargets = (composerTargetStepIds.length
    ? composerTargetStepIds
    : selectedScratchStepIds.length
      ? selectedScratchStepIds
      : current && !current.targetStepIds?.length
        ? [current.stepId]
        : [])
    .map((stepId) => rawSteps.find((step) => step.stepId === stepId))
    .filter((step): step is CandidateStep => Boolean(step));
  useEffect(() => {
    setFocus(0);
    setVideoModel("");
    setModelOnly(false);
    if (task?.gate.startsWith("scratch_"))
      setCorrectionKind(
        task.gate === "scratch_final_acceptance"
          ? "video_regeneration"
          : task.gate === "scratch_creative_plan"
            ? "script_patch"
            : "staging_image_regeneration",
      );
    setSelectedScratchStepIds([]);
    setResourceOverrideId("");
    setPendingScratchEdits([]);
    setComposerTargetStepIds([]);
    setMessage("");
  }, [task?.id, task?.gate, candidate?.sha256]);

  useEffect(() => {
    setShowProvisionalCorrection(true);
  }, [task?.provisionalCorrection?.transactionId]);

  useEffect(() => {
    if (!task?.gate.startsWith("scratch_") || message.trim() || !current) return;
    if (current.stepId === "script") setCorrectionKind("script_patch");
    else if (current.stepId === "compact-storyboard") setCorrectionKind("storyboard_patch");
    else if (current.stepId.startsWith("voice-")) setCorrectionKind("voice_selection");
    else if (current.stepId.startsWith("sheet-") || current.stepId.startsWith("character-sheet-")) setCorrectionKind("character_sheet_regeneration");
    else if (current.stepId === "protagonist-profile" || current.stepId === "supporting-characters") setCorrectionKind("character_profile_patch");
    else if (current.stepId === "selected-style" || current.stepId === "style") setCorrectionKind("style_patch");
  }, [current?.stepId, message, task?.gate]);
  useEffect(() => {
    if (!message.trim()) { setVideoModel(""); setModelOnly(false); }
  }, [current?.stepId]);

  const addCurrentScratchChange = () => {
    if (!task || !candidate || !current || !message.trim()) return;
    if (correctionKind === "model_override" && !videoModel.trim()) throw new Error("Choose the video model to change to.");
    const targets = composerTargets;
    if (!targets.length) throw new Error("Choose the exact component or storyboard unit this change applies to.");
    const literalCorrection = ["literal_image_replacement", "literal_video_replacement", "audio_replacement"].includes(correctionKind);
    if (literalCorrection && (!replacementFile || targets.length !== 1)) throw new Error("Choose one replacement file and one exact target item.");
    if (["voice_selection", "style_patch"].includes(correctionKind) && !resourceOverrideId) throw new Error("Choose the exact approved resource to use.");
    const additions: PendingScratchEdit[] = targets.map((step) => ({
      edit_id: `edit-${crypto.randomUUID()}`,
      kind: correctionKind,
      target: {
        component_id: step.stepId,
        cut_id: step.stepId.startsWith("cut-") ? step.stepId.replace(/-end$/u, "") : null,
        asset_sha256: step.artifactSha256,
        timestamp_seconds: typeof step.content.timestampSeconds === "number" ? step.content.timestampSeconds : null,
      },
      instruction: message.trim(),
      output_mode: correctionKind === "staging_image_regeneration" && step.stepId.startsWith("cut-") ? imageCorrectionMode : scratchCorrectionOutputMode(correctionKind),
      model_override: (correctionKind === "video_regeneration" || correctionKind === "model_override") ? scratchVideoModelOverride(videoModel, modelOnly) : null,
      resource_override_id: ["voice_selection", "style_patch"].includes(correctionKind) ? resourceOverrideId : null,
      uploaded_asset: null,
      provider_authorized: scratchCorrectionInvokesProvider(correctionKind),
      targetTitle: step.title,
      replacementFile: literalCorrection ? replacementFile : null,
    }));
    setPendingScratchEdits((currentEdits) => upsertPendingScratchEdits(currentEdits, additions));
    setMessage("");
    setComposerTargetStepIds([]);
    setSelectedScratchStepIds([]);
    setReplacementFile(null);
    setVideoModel("");
    setModelOnly(false);
    setError("");
  };

  const submitPendingScratchChanges = async () => {
    if (!task || !candidate || !pendingScratchEdits.length) return;
    if (message.trim()) { setError("Add the current cut’s change or clear its draft before submitting."); return; }
    setBusy(true);
    setError("");
    try {
      const edits = await Promise.all(pendingScratchEdits.map(async (draft): Promise<ScratchCorrectionEdit> => {
        const { targetTitle: _targetTitle, resourceLabel: _resourceLabel, replacementFile: draftFile, ...edit } = draft;
        const literalCorrection = ["literal_image_replacement", "literal_video_replacement", "audio_replacement"].includes(edit.kind);
        const uploadedAsset = literalCorrection && draftFile
          ? await uploadScratchCorrectionAsset(draftFile, task.id, candidate.sha256)
          : edit.uploaded_asset ?? null;
        if (literalCorrection && !uploadedAsset) throw new Error(`The pending ${scratchCorrectionLabel(edit.kind).toLowerCase()} change lost its replacement file. Remove it and add the change again.`);
        return { ...edit, uploaded_asset: uploadedAsset };
      }));
      const authorizationSha256 = await browserSha256({ taskId: task.id, candidate: candidate.sha256, edits });
      const feedback = {
        message: scratchPendingFeedbackMessage(pendingScratchEdits),
        idempotencyKey: `feedback-${crypto.randomUUID()}`,
        edits,
        authorizationSha256,
        activeInteractionMs: readActiveInteractionMs(),
      };
      if (provisionalCorrection && showProvisionalCorrection) {
        await api.reviseScratchProvisionalCorrection(task.id, {
          ...feedback,
          transactionId: provisionalCorrection.transactionId,
          expectedSourceCandidateSha256: provisionalCorrection.sourceCandidateSha256,
          expectedReplacementCandidateSha256: provisionalCorrection.candidate.sha256,
        });
      } else {
        await api.productionMessage(task.id, { ...feedback, expectedCandidateSha256: candidate.sha256 });
      }
      resetActiveInteraction();
      setPendingScratchEdits([]);
        setResourceOverrideId("");
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not queue these targeted changes.");
    } finally {
      setBusy(false);
    }
  };

  const send = async (event: FormEvent) => {
    event.preventDefault();
    if (!task || !candidate || !current || !message.trim()) return;
    if (approvalMessage && approvalBlockReason) {
      setError(approvalBlockReason);
      return;
    }
    if (task.gate.startsWith("scratch_") && !approvalMessage) {
      try {
        addCurrentScratchChange();
      } catch (reason) {
        setError(reason instanceof Error ? reason.message : "Could not add this change.");
      }
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.productionMessage(task.id, {
        message: message.trim(),
        expectedCandidateSha256: candidate.sha256,
        idempotencyKey: `feedback-${crypto.randomUUID()}`,
        activeInteractionMs: task.gate.startsWith("scratch_") ? readActiveInteractionMs() : undefined,
      });
      resetActiveInteraction();
      setMessage("");
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not queue this feedback.");
    } finally {
      setBusy(false);
    }
  };
  // One submission is the whole cascade's authorization. The key is fixed per
  // composed draft, so a double click or a retried request re-sends the same
  // action instead of creating another job.
  const submitWhiteboardFeedback = async (edits: ScratchCorrectionEdit[], idempotencyKey: string): Promise<boolean> => {
    if (!task || !candidate) return false;
    setBusy(true);
    setError("");
    try {
      const authorizationSha256 = await browserSha256({ taskId: task.id, candidate: candidate.sha256, edits });
      await api.productionMessage(task.id, {
        message: `Whiteboard feedback on ${edits.map(edit => edit.target.component_id).join(", ")}`,
        expectedCandidateSha256: candidate.sha256, idempotencyKey, edits, authorizationSha256,
        activeInteractionMs: readActiveInteractionMs(),
      });
      resetActiveInteraction();
      await load();
      return true;
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not submit this feedback.");
      return false;
    } finally {
      setBusy(false);
    }
  };
  const approve = async () => {
    if (!task || !candidate) return;
    if (approvalBlockReason) {
      setError(approvalBlockReason);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await api.approveProductionReview(task.id, {
        expectedCandidateSha256: candidate.sha256,
        idempotencyKey: `approve-${task.id}-${candidate.sha256}`,
        selectedStaticCandidateStepIds: staticCandidateReview
          ? selectedStaticCandidateStepIds
          : undefined,
        activeInteractionMs: task.gate.startsWith("scratch_") ? readActiveInteractionMs() : undefined,
      });
      resetActiveInteraction();
      await load();
    } catch (reason) {
      setError(
        reason instanceof Error ? reason.message : "Could not queue approval.",
      );
    } finally {
      setBusy(false);
    }
  };
  const decideProvisionalCorrection = async (decision: "accept" | "discard") => {
    if (!task || !provisionalCorrection || !task.currentCandidate) return;
    setBusy(true);
    setError("");
    try {
      await api.decideScratchProvisionalCorrection(task.id, decision, {
        transactionId: provisionalCorrection.transactionId,
        expectedSourceCandidateSha256: provisionalCorrection.sourceCandidateSha256,
        expectedReplacementCandidateSha256: provisionalCorrection.candidate.sha256,
        idempotencyKey: `${decision}-${provisionalCorrection.transactionId}-${provisionalCorrection.candidate.sha256}`,
      });
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not save this regenerated-clip decision.");
    } finally {
      setBusy(false);
    }
  };
  const selectStaticCandidate = () => {
    if (!current?.stepId) return;
    const member = current.stepId.split("-candidate-")[0];
    setSelectedStaticCandidateStepIds((currentSelection) => [
      ...currentSelection.filter(
        (stepId) => stepId.split("-candidate-")[0] !== member,
      ),
      current.stepId,
    ]);
  };
  if (!task && !error)
    return (
      <div className="production-review-loading">
        <span />
        <span />
      </div>
    );
  if (!task)
    return (
      <div className="page-error">
        <h1>Review unavailable</h1>
        <p>{error}</p>
        <Link to="/kanban">
          <ArrowLeft size={16} />
          Return to Kanban
        </Link>
      </div>
    );
  if (candidate?.content.schemaVersion === "scratch-whiteboard-package-v1") return <WhiteboardPackageSurface
    key={candidate.sha256} candidate={candidate} task={task} enabled={actionsEnabled} busy={busy} error={error}
    onApprove={approve} onChanged={load}
  />;
  if (candidate?.content.schemaVersion === "scratch-whiteboard-final-v1") return <WhiteboardFinalSurface
    key={candidate.sha256} candidate={candidate} task={task} enabled={actionsEnabled} busy={busy} error={error}
    onApprove={approve} onFeedback={submitWhiteboardFeedback}
  />;
  return (
    <div className="production-review-page">
      <header className="review-command-bar">
        <Link to="/kanban">
          <ArrowLeft size={16} />
          Kanban
        </Link>
        <div>
          <strong>{task.brand.name}</strong>
          <span>
            {task.product.name} · {task.variationSetPublicId}
          </span>
        </div>
        <div className="review-step-status">
          <span>
            {focus + 1} of {Math.max(1, steps.length)}
          </span>
          {steps.length > 1 && (
            <>
              <button
                className="icon-button"
                disabled={focus === 0}
                onClick={() => setFocus((value) => Math.max(0, value - 1))}
                aria-label="Previous review item"
              >
                <ChevronLeft size={17} />
              </button>
              <button
                className="icon-button"
                disabled={focus >= steps.length - 1}
                onClick={() =>
                  setFocus((value) => Math.min(steps.length - 1, value + 1))
                }
                aria-label="Next review item"
              >
                <ChevronRight size={17} />
              </button>
            </>
          )}
        </div>
        {!nativeSpeakerDirections && <button
          className="primary-button"
          disabled={
            !actionsEnabled ||
            busy ||
            task.status !== "open" ||
            candidate?.validationStatus !== "valid" ||
            !reviewable ||
            !staticSelectionReady ||
            Boolean(approvalBlockReason)
          }
          title={approvalBlockReason ?? undefined}
          onClick={() => void (provisionalCorrection ? decideProvisionalCorrection("accept") : approve())}
        >
          <Check size={17} />
          {provisionalCorrection
            ? provisionalOutcome.acceptLabel
            : approvalProgress
            ? approvalProgress.buttonLabel
            : approvalBlockReason
            ? pendingScratchEdits.length > 0
              ? `Submit ${pendingScratchEdits.length} change${pendingScratchEdits.length === 1 ? "" : "s"} first`
              : "Add or clear draft first"
            : staticCandidateReview
              ? "Finalize selected candidates"
              : "Approve current output"}
        </button>}
      </header>
      {error && (
        <div className="error-banner review-error" role="alert">
          <div>
            <strong>Action stopped</strong>
            <p>{error}</p>
          </div>
        </div>
      )}
      {correctionProgress && (
        <div className="review-processing-banner" role="status">
          <LoaderCircle className="spin" size={18} />
          <div>
            <strong>{correctionProgress.title}</strong>
            <p>{correctionProgress.detail}</p>
          </div>
        </div>
      )}
      {approvalProgress && (
        <div className="review-processing-banner" role="status">
          <LoaderCircle className="spin" size={18} />
          <div>
            <strong>{approvalProgress.title}</strong>
            <p>{approvalProgress.detail}</p>
          </div>
        </div>
      )}
      {provisionalCorrection && (
        <section className="review-provisional-decision" role="status" aria-live="polite">
          <div>
            <strong>{provisionalOutcome.title}</strong>
            <p>{provisionalOutcome.detail}</p>
          </div>
          <div className="review-provisional-switch" aria-label="Compare correction versions">
            <button type="button" className={showProvisionalCorrection ? "is-active" : ""} onClick={() => setShowProvisionalCorrection(true)}>{scratchProvisionalDecisionCopy.proposedLabel}</button>
            <button type="button" className={!showProvisionalCorrection ? "is-active" : ""} onClick={() => setShowProvisionalCorrection(false)}>{scratchProvisionalDecisionCopy.currentLabel}</button>
          </div>
          <button type="button" className="secondary-button" disabled={!actionsEnabled || busy} onClick={() => void decideProvisionalCorrection("discard")}>{scratchProvisionalDecisionCopy.discardLabel}</button>
        </section>
      )}
      <div className="production-review-layout">
        <section className={`review-canvas${currentChanged ? " review-canvas--changed" : ""}`} aria-label="Current review output">
          <header>
            <div>
              <span>{gateLabel(task.gate)}</span>
              <strong>{current?.title ?? task.product.name}</strong>
            </div>
            <div>
              <ShieldCheck size={15} />
              <span>r{candidate?.revision ?? 0}</span>
              <code>{candidate?.sha256.slice(0, 12)}</code>
            </div>
          </header>
          {candidate && current ? (
            <CandidateSurface
              candidate={candidate}
              step={current}
              gate={task.gate}
              task={task}
              selectedVoiceId={selectedVoiceId ?? ""}
              selectedStepIds={selectedScratchStepIds}
              onSelectVoice={(voiceId, voiceLabel, targetComponentId) => {
                const target = rawSteps.find((step) => step.stepId === (targetComponentId || current.stepId));
                if (!target) {
                  setError("The voice review item is no longer part of the current candidate. Reload before choosing a voice.");
                  return;
                }
                setResourceOverrideId(voiceId);
                setPendingScratchEdits((currentEdits) => upsertPendingScratchEdits(currentEdits, [{
                  edit_id: `edit-${crypto.randomUUID()}`,
                  kind: "voice_selection",
                  target: { component_id: target.stepId, asset_sha256: target.artifactSha256, cut_id: null, timestamp_seconds: null },
                  instruction: `Use ${voiceLabel} for this ad.`,
                  output_mode: "recompile",
                  model_override: null,
                  resource_override_id: voiceId,
                  uploaded_asset: null,
                  provider_authorized: false,
                  targetTitle: target.title,
                  resourceLabel: voiceLabel,
                }]));
                setError("");
              }}
              onToggleStep={(stepId, checked) =>
                setSelectedScratchStepIds((value) =>
                  checked
                    ? [...new Set([...value, stepId])]
                    : value.filter((valueStepId) => valueStepId !== stepId),
                )
              }
            />
          ) : (
            <div className="review-canvas-empty">
              The authoritative runner has not projected a candidate yet.
            </div>
          )}
          {currentChanged && (
            <div className="review-change-diff">
              <strong>Changed in this revision</strong>
              {changedItems.filter((item) => currentTargetIds.includes(stringValue(item.componentId))).map((item, index) => (
                <p key={`${stringValue(item.editId)}-${index}`}><code>{shortHash(stringValue(item.beforeSha256))}</code> → <code>{shortHash(stringValue(item.afterSha256))}</code> · {stringValue(item.instruction)}</p>
              ))}
            </div>
          )}
          {automaticRepairs.length > 0 && focus === 0 && (
            <div className="review-change-diff">
              <strong>Automatic repairs applied</strong>
              {automaticRepairs.map((item, index) => <p key={`${stringValue(item.findingId)}-${index}`}><code>{shortHash(stringValue(item.beforeSha256))}</code> → <code>{shortHash(stringValue(item.afterSha256))}</code> · {stringValue(item.status).replaceAll("_", " ")}</p>)}
              <small>Each repair is append-only and can be reversed by requesting the prior SHA-256 shown here.</small>
            </div>
          )}
          {currentObjectiveFindings.length > 0 && (
            <div className="review-objective-findings" role="alert">
              <strong>Objective QA still needs your decision</strong>
              <p>The system already used the one automatic repair allowed for these clips, or could not safely submit it within the authorized budget. Review these exact findings before approval.</p>
              <ol>
                {currentObjectiveFindings.map((finding, index) => (
                  <li key={`${stringValue(finding.finding_id)}-${index}`}>
                    <strong>{stringValue(finding.cut_id)}</strong>
                    <span>{stringValue(finding.literal_evidence)}</span>
                  </li>
                ))}
              </ol>
            </div>
          )}
          {staticCandidateReview && current && (
            <footer>
              <button
                className="secondary-button"
                disabled={!actionsEnabled || busy}
                onClick={selectStaticCandidate}
              >
                <Check size={16} />
                {selectedStaticCandidateStepIds.includes(current.stepId)
                  ? "Selected for finalization"
                  : "Select this candidate"}
              </button>
              <span>
                {selectedStaticCandidateStepIds.length} of{" "}
                {staticMembers.length} variation
                {staticMembers.length === 1 ? "" : "s"} selected
              </span>
            </footer>
          )}
          {task.artifactScope.shared && (
            <footer>
              Reviewed once · used by {task.artifactScope.memberIds.length}{" "}
              variants
            </footer>
          )}
        </section>
        <aside className="review-chat" aria-label="Production feedback">
          <header>
            <MessageSquareText />
            <div>
              <strong>Feedback</strong>
              <span>
                {actionsEnabled
                  ? "Full relevant run context · writes limited to this task"
                  : "Read-only in this environment"}
              </span>
            </div>
          </header>
          <div className="review-message-list">
            {task.messages?.length ? (
              task.messages.map((item) => (
                <article
                  className={`review-message review-message--${item.role}`}
                  key={item.id}
                >
                  <span>
                    {item.role === "operator"
                      ? "You"
                      : item.role === "assistant"
                        ? "System"
                        : "Workflow"}
                  </span>
                  <p>{item.content}</p>
                  <small>
                    {item.status.replaceAll("_", " ")} ·{" "}
                    {new Date(item.createdAt).toLocaleTimeString([], {
                      hour: "numeric",
                      minute: "2-digit",
                    })}
                  </small>
                </article>
              ))
            ) : (
              <div className="review-chat-empty">
                <p>
                  Ask for an exact change, or send exactly <code>approve</code>{" "}
                  or <code>approved</code>.
                </p>
              </div>
            )}
          </div>
          {provisionalCorrection && (
            <section className="review-provisional-chat-decision">
              <strong>Choose what happens to this newest set</strong>
              <p><b>{provisionalOutcome.acceptLabel}</b> keeps every clip currently shown. <b>{scratchProvisionalDecisionCopy.discardLabel}</b> discards this whole proposal. To keep most of this set and change one clip, add targeted feedback below.</p>
              <button type="button" className="primary-button" disabled={!actionsEnabled || busy} onClick={() => void decideProvisionalCorrection("accept")}>
                {busy ? <LoaderCircle className="spin" size={16} /> : <Check size={16} />}
                {provisionalOutcome.acceptLabel}
              </button>
              <button type="button" className="secondary-button" disabled={!actionsEnabled || busy} onClick={() => void decideProvisionalCorrection("discard")}>{scratchProvisionalDecisionCopy.discardLabel}</button>
            </section>
          )}
          {nativeSpeakerDirections && <div className="review-chat-empty"><p>These are saved speaker directions, not a voice audition. Review actual voices and lip-sync in the final clips. No preset selection or detached audio replacement is available.</p></div>}
          {!nativeSpeakerDirections && (!provisionalCorrection || showProvisionalCorrection) && <form onSubmit={(event) => void send(event)}>
            {provisionalCorrection && <div className="review-provisional-revision-note"><strong>{scratchProvisionalDecisionCopy.reviseHeading}</strong><p>Target only the clip that still needs work. Every other newest proposed clip will be preserved exactly, and your current review set remains available until the revised package is ready.</p></div>}
            {scratchCorrection && pendingScratchEdits.length > 0 && (
              <section className="scratch-pending-changes" aria-live="polite">
                <header>
                  <div>
                    <span>Pending changes</span>
                    <strong>{pendingScratchEdits.length} change{pendingScratchEdits.length === 1 ? "" : "s"} ready</strong>
                  </div>
                  <small>Nothing runs until you submit this batch.</small>
                </header>
                <ol>
                  {pendingScratchEdits.map((edit) => (
                    <li key={edit.edit_id}>
                      <div>
                        <span>{scratchCorrectionLabel(edit.kind)}</span>
                        <strong>{edit.resourceLabel ?? edit.targetTitle}</strong>
                        <p>{edit.instruction}</p>
                        <small>{edit.provider_authorized ? "Provider generation after submission" : "No provider call"}</small>
                      </div>
                      <button
                        type="button"
                        aria-label={`Remove ${scratchCorrectionLabel(edit.kind)} change`}
                        onClick={() => {
                          setPendingScratchEdits((currentEdits) => currentEdits.filter((item) => item.edit_id !== edit.edit_id));
                          if (edit.kind === "voice_selection") setResourceOverrideId("");
                        }}
                        disabled={busy || task.status !== "open"}
                      >
                        <Trash2 size={15} />
                        <span>Remove</span>
                      </button>
                    </li>
                  ))}
                </ol>
                {message.trim() && <p>Add the current cut’s change below, or clear its draft, before submitting.</p>}
                <button type="button" className="primary-button scratch-submit-batch" onClick={() => void submitPendingScratchChanges()} disabled={!actionsEnabled || busy || task.status !== "open" || Boolean(message.trim())}>
                  {busy ? <LoaderCircle className="spin" size={16} /> : <Send size={16} />}
                  {provisionalCorrection ? "Revise newest set with" : "Submit"} {pendingScratchEdits.length} change{pendingScratchEdits.length === 1 ? "" : "s"}
                </button>
              </section>
            )}
            {scratchCorrection && !approvalMessage && (
              <label className="scratch-correction-scope">
                <span>{pendingScratchEdits.length ? "Add another change" : "Correction scope"}</span>
                <select
                  value={correctionKind}
                  onChange={(event) => {
                    setCorrectionKind(event.target.value as ScratchCorrectionKind);
                    setResourceOverrideId("");
                    setSelectedScratchStepIds([]);
                  }}
                  disabled={busy || task.status !== "open"}
                >
                  <option value="staging_image_regeneration">Correct selected staging image</option>
                  <option value="video_regeneration">Regenerate selected video</option>
                  <option value="script_patch">Patch exact script line</option>
                  <option value="storyboard_patch">Patch selected storyboard unit</option>
                  <option value="character_profile_patch">Patch character profile and dependent sheet</option>
                  <option value="character_sheet_regeneration">Regenerate selected character sheet</option>
                  {task.gate === "scratch_creative_plan" && current?.stepId.startsWith("sheet-character-supporting-") && <option value="character_reclassification">Remove character sheet — keep as material/prop</option>}
                  <option value="style_patch">Patch Style-dependent assets</option>
                  {!nativeDialogue && !scriptedCue && <option value="voice_selection">Switch selected approved voice</option>}
                  <option value="literal_image_replacement">Replace with uploaded image</option>
                  <option value="literal_video_replacement">Replace with uploaded video</option>
                  {!nativeDialogue && <option value="audio_replacement">Replace with uploaded audio</option>}
                  <option value="caption_only">Caption only — no provider</option>
                  <option value="assembly_only">Assembly only — no provider</option>
                </select>
                {correctionKind === "staging_image_regeneration" && current?.stepId.startsWith("cut-") && <>
                  <span>Image correction</span>
                  <select aria-label="Image correction method" value={imageCorrectionMode} disabled={busy || task.status !== "open"}
                    onChange={(event) => setImageCorrectionMode(event.target.value as "patch" | "regenerate")}>
                    <option value="patch">Edit existing image — preserve everything else</option>
                    <option value="regenerate">Replan scene — change the requested composition</option>
                  </select>
                </>}
              </label>
            )}
            {task.gate.startsWith("scratch_") && !approvalMessage && ["literal_image_replacement", "literal_video_replacement", "audio_replacement"].includes(correctionKind) && (
              <label className="scratch-correction-scope">
                <span>Exact replacement asset</span>
                <input type="file" accept={correctionKind === "literal_image_replacement" ? "image/*" : correctionKind === "literal_video_replacement" ? "video/*" : "audio/*"} onChange={(event) => setReplacementFile(event.target.files?.[0] ?? null)} disabled={busy || task.status !== "open"} />
              </label>
            )}
            {task.gate === "scratch_creative_plan" && !approvalMessage && correctionKind === "voice_selection" && !current?.stepId.startsWith("voice-") && (
              <div className="scratch-correction-scope scratch-correction-scope--summary">
                <span>Voice choice</span>
                <p>Open the Voice review item to audition every available female voice and choose one.</p>
              </div>
            )}
            {task.gate === "scratch_creative_plan" && !approvalMessage && correctionKind === "voice_selection" && current?.stepId.startsWith("voice-") && (
              <div className="scratch-correction-scope scratch-correction-scope--summary">
                <span>Voice selection</span>
                <p>Choosing a voice above immediately adds or updates its Pending change. No additional feedback text is required.</p>
              </div>
            )}
            {task.gate === "scratch_creative_plan" && !approvalMessage && correctionKind === "style_patch" && (
              <label className="scratch-correction-scope">
                <span>Calibrated Style</span>
                <select value={resourceOverrideId} onChange={(event) => setResourceOverrideId(event.target.value)} disabled={busy || task.status !== "open"}>
                  <option value="">Choose a Style…</option>
                  {(task.correctionOptions?.styles ?? []).map((style) => <option key={style.id} value={style.id}>{style.label} · {style.publicId}</option>)}
                </select>
              </label>
            )}
            {task.gate.startsWith("scratch_") && !approvalMessage && (correctionKind === "video_regeneration" || correctionKind === "model_override") && (
              <label className="scratch-correction-scope">
                <span>Video model override (optional)</span>
                <input list="scratch-video-models" placeholder="Use this cut’s approved model" value={videoModel} onChange={(event) => setVideoModel(event.target.value)} disabled={busy || task.status !== "open"} />
                <datalist id="scratch-video-models">
                  <option value="kie-veo-3-1-lite">Veo 3.1 Lite</option>
                  <option value="kie-google-omni">Google Omni</option>
                  <option value="kie-kling-3">Kling 3.0</option>
                </datalist>
                <label><input type="checkbox" disabled={!videoModel.trim()} checked={modelOnly} onChange={(event) => setModelOnly(event.target.checked)} /> Use this model only</label>
              </label>
            )}
            {correctionKind !== "voice_selection" && task.gate.startsWith("scratch_") && current && !approvalMessage && current.targetStepIds?.length ? (
              <div className="scratch-correction-scope scratch-correction-scope--summary">
                <span>Targeted storyboard edits</span>
                <p>{selectedScratchStepIds.length ? `${selectedScratchStepIds.length} storyboard unit${selectedScratchStepIds.length === 1 ? "" : "s"} selected above.` : "Choose one or more storyboard units above before sending a correction."}</p>
              </div>
            ) : null}
            {correctionKind !== "voice_selection" && task.gate.startsWith("scratch_") && composerTargets.length > 0 && !approvalMessage && (
              <div className="scratch-correction-scope scratch-correction-scope--summary scratch-composer-target">
                <span>Adding change for</span>
                <p>{composerTargets.map((step) => step.title).join(" · ")}</p>
              </div>
            )}
            {(correctionKind !== "voice_selection" || !scratchCorrection || approvalMessage) && <>
              <textarea
                rows={4}
                value={message}
                onChange={(event) => {
                  const nextMessage = event.target.value;
                  if (!message.trim() && nextMessage.trim()) {
                    const targetIds = selectedScratchStepIds.length
                      ? selectedScratchStepIds
                      : current && !current.targetStepIds?.length
                        ? [current.stepId]
                        : [];
                    setComposerTargetStepIds(targetIds);
                  } else if (!nextMessage.trim()) setComposerTargetStepIds([]);
                  setMessage(nextMessage);
                }}
                placeholder={
                  invokesProvider
                    ? "Describe the exact regeneration needed…"
                    : "Describe the exact change…"
                }
                disabled={!actionsEnabled || busy || task.status !== "open"}
              />
              <div>
              <span>
                {approvalMessage
                  ? "Approve the current validated candidate"
                  : invokesProvider
                    ? `${affectedItemCount} affected item${affectedItemCount === 1 ? "" : "s"} · ${scriptedCue ? "scripted cue regeneration" : current?.contentKind === "audio" ? "ElevenLabs Voice Changer" : "provider regeneration"}`
                    : staticPlan
                      ? "Static plan correction route"
                      : current?.contentKind === "script"
                        ? message
                            .trim()
                            .toLowerCase()
                            .startsWith("replace with:")
                          ? "Exact operator replacement"
                          : "DeepSeek target-writing route"
                        : "Deterministic production route"}
              </span>
                <button
                className={
                  invokesProvider && !approvalMessage
                    ? "primary-button"
                    : "secondary-button"
                }
                disabled={
                  !actionsEnabled ||
                  busy ||
                  !message.trim() ||
                  task.status !== "open"
                }
              >
                {busy ? (
                  <LoaderCircle className="spin" size={16} />
                ) : approvalMessage ? (
                  <Check size={16} />
                ) : (
                  <Send size={16} />
                )}
                  {approvalMessage
                  ? "Approve"
                  : scratchCorrection
                    ? "Add change"
                  : invokesProvider
                    ? "Send & regenerate"
                    : "Send feedback"}
                </button>
              </div>
            </>}
          </form>}
        </aside>
      </div>
    </div>
  );
}

type CandidateStep = {
  position: number;
  stepId: string;
  title: string;
  contentKind: ProductionReviewCandidate["contentKind"];
  content: Record<string, unknown>;
  artifactSha256: string;
  artifactUrl: string | null;
  targetStepIds?: string[];
};
function candidateSteps(
  candidate: ProductionReviewCandidate | null,
): CandidateStep[] {
  if (!candidate) return [];
  if (candidate.items?.length)
    return candidate.items.map((item) => ({
      position: item.position,
      stepId: item.stepId,
      title: item.title,
      contentKind: item.contentKind,
      content: item.content,
      artifactSha256: item.artifactSha256,
      artifactUrl: item.artifactUrl,
    }));
  return [
    {
      position: 1,
      stepId: "current-output",
      title: String(candidate.content.title ?? "Current output"),
      contentKind: candidate.contentKind,
      content: candidate.content,
      artifactSha256: candidate.sha256,
      artifactUrl: candidate.artifactUrl,
    },
  ];
}

export function reviewSteps(
  candidate: ProductionReviewCandidate | null,
  gate?: ProductionReviewTask["gate"],
): CandidateStep[] {
  const raw = candidateSteps(candidate);
  if (!candidate || gate !== "scratch_creative_plan") return primaryCompilationFirst(raw);
  const storyboard = raw.filter((step) => step.stepId.startsWith("storyboard-"));
  if (storyboard.length < 2) return primaryCompilationFirst(raw);
  const firstStoryboardIndex = raw.findIndex((step) => step.stepId.startsWith("storyboard-"));
  return primaryCompilationFirst(raw
    .flatMap((step, index): CandidateStep[] => {
      if (!step.stepId.startsWith("storyboard-")) return [step];
      if (index !== firstStoryboardIndex) return [];
      return [{
        position: step.position,
        stepId: "compact-storyboard",
        title: "Compact storyboard",
        contentKind: "bundle",
        content: {
          schemaVersion: "scratch-compact-storyboard-review-v1",
          units: storyboard.map((unit) => ({
            stepId: unit.stepId,
            title: unit.title,
            artifactSha256: unit.artifactSha256,
            content: unit.content,
          })),
        },
        artifactSha256: candidate.sha256,
        artifactUrl: null,
        targetStepIds: storyboard.map((unit) => unit.stepId),
      }];
    })
    .map((step, index) => ({ ...step, position: index + 1 })));
}

function primaryCompilationFirst(steps: CandidateStep[]): CandidateStep[] {
  return steps
    .map((step, sourceIndex) => ({ step, sourceIndex, priority: reviewStepPriority(step) }))
    .sort((left, right) => left.priority - right.priority || left.sourceIndex - right.sourceIndex)
    .map(({ step }) => step);
}

function reviewStepPriority(step: CandidateStep): number {
  if (step.content.reviewRole === "primary_compilation") return 0;
  if (step.stepId === "complete-staging-draft" || step.stepId === "canonical-final-render") return 0;
  if (step.content.reviewRole === "delivery_derivative" || step.stepId === "complete-final-ad") return 1;
  if (step.contentKind === "final" && step.artifactUrl) return 2;
  return 3;
}

export function ScratchExecutionAuthority({ content }: { content: Record<string, unknown> }) {
  const contract = objectValue(content.cutContract);
  if (stringValue(contract.schema_version) !== "scratch-cut-execution-contract-v1") return null;
  const timing = objectValue(contract.timing);
  const routing = objectValue(contract.routing);
  const noSplit = timing.no_split === true;
  const route = stringValue(routing.selected_route);
  return <section className="scratch-source-review">
    <header><span>Compiled execution</span><strong>Timing and route authority</strong></header>
    <dl>
      <div><dt>Timing mode</dt><dd>{humanLabel(stringValue(timing.timing_mode, "natural"))}</dd></div>
      <div><dt>Requested duration</dt><dd>{numberValue(timing.requested_duration_seconds).toFixed(numberValue(timing.requested_duration_seconds) % 1 ? 1 : 0)} seconds</dd></div>
      <div><dt>Retained duration</dt><dd>{numberValue(timing.retained_duration_seconds).toFixed(numberValue(timing.retained_duration_seconds) % 1 ? 1 : 0)} seconds</dd></div>
      <div><dt>Provider duration</dt><dd>{numberValue(timing.provider_duration_seconds).toFixed(numberValue(timing.provider_duration_seconds) % 1 ? 1 : 0)} seconds</dd></div>
      <div><dt>Continuity</dt><dd>{noSplit ? "No split · one retained shot" : "Cuts permitted inside the scene duration"}</dd></div>
      <div><dt>Selected route</dt><dd>{route}</dd></div>
      {stringValue(routing.approved_fallback) && <div><dt>Approved fallback</dt><dd>{stringValue(routing.approved_fallback)}</dd></div>}
      <div><dt>Source treatment</dt><dd>{humanLabel(stringValue(timing.source_treatment, "generated_action"))}</dd></div>
    </dl>
    {route === "local-deterministic-video" && <p>Local deterministic hold/zoom only · no generated subject or object action.</p>}
  </section>;
}

function CandidateSurface({
  candidate,
  step,
  gate,
  task,
  selectedVoiceId,
  selectedStepIds,
  onSelectVoice,
  onToggleStep,
}: {
  candidate: ProductionReviewCandidate;
  step: CandidateStep;
  gate: ProductionReviewTask["gate"];
  task: ProductionReviewTask;
  selectedVoiceId: string;
  selectedStepIds: string[];
  onSelectVoice: (voiceId: string, voiceLabel: string, targetComponentId?: string) => void;
  onToggleStep: (stepId: string, checked: boolean) => void;
}) {
  const staticPlan = staticPlanReview(step.content);
  if (staticPlan) return <StaticCreativePlanSurface review={staticPlan} />;
  if (gate === "static_creative_plan")
    return (
      <div className="review-canvas-empty">
        <div>
          <strong>Static plan is incomplete</strong>
          <p>
            The candidate does not contain its complete copy lock, two request
            set, and identity inputs. Approval is disabled until the runner
            projects those exact artifacts.
          </p>
        </div>
      </div>
    );
  if (gate === "scratch_creative_plan")
    return (
      <ScratchCreativeComponentSurface
        step={step}
        voices={task.correctionOptions?.voices ?? []}
        selectedVoiceId={selectedVoiceId}
        selectedStepIds={selectedStepIds}
        onSelectVoice={onSelectVoice}

        onToggleStep={onToggleStep}
      />
    );
  if (step.content.schemaVersion === "scratch-review-candidate-v1")
    return <ScratchCreativePlanSurface content={step.content} />;
  if (step.contentKind === "image" && step.artifactUrl)
    return (
      <>
        <div className="review-media review-media--image">
          <div className="review-static-frame">
            <img src={step.artifactUrl} alt={step.title} />
            {step.content.workflowProfile === "static_ad" && (
              <div
                className="review-safe-area"
                aria-label="Centered 4 by 5 safe area"
              >
                <span>4:5 safe area · 48 px text inset</span>
              </div>
            )}
          </div>
        </div>
        <ScratchExecutionAuthority content={step.content} />
      </>
    );
  if (
    (step.contentKind === "video" || step.contentKind === "final") &&
    step.artifactUrl
  )
    return (
      <>
        <div className="review-media review-media--video">
          <video src={step.artifactUrl} controls playsInline />
        </div>
        <ScratchExecutionAuthority content={step.content} />
      </>
    );
  if (step.contentKind === "audio" && step.artifactUrl && stringValue(step.content.cueId))
    return <ScratchCueAudio content={step.content} artifactUrl={step.artifactUrl} />;
  if (step.contentKind === "audio" && step.artifactUrl)
    return (
      <div className="review-audio">
        <audio src={step.artifactUrl} controls />
        <pre>{String(step.content.transcript ?? "")}</pre>
      </div>
    );
  if (step.contentKind === "script")
    return (
      <div className="review-script">
        <pre>{String(step.content.completeScript ?? step.content.script ?? step.content.text ?? "")}</pre>
      </div>
    );
  return (
    <div className="review-bundle">
      <pre>{JSON.stringify(step.content ?? candidate.content, null, 2)}</pre>
    </div>
  );
}

type StaticPlanReview = {
  plan: Record<string, unknown>;
  copyLock: Record<string, unknown>;
  requests: Record<string, unknown>[];
  identityAssets: Record<string, unknown>[];
  slots: Record<string, unknown>[];
  omissions: string[];
};

export function staticPlanReview(
  content: Record<string, unknown> | undefined,
): StaticPlanReview | null {
  const plan = objectValue(content?.plan);
  const copyLock = objectValue(content?.copyLock);
  const requests = arrayValue(plan.candidate_requests).map(objectValue);
  const identityAssets = arrayValue(plan.identity_assets).map(objectValue);
  const slots = arrayValue(copyLock.slots).map(objectValue);
  if (
    plan.schema_version !== "static-ad-plan-v2" ||
    !stringValue(objectValue(plan.deterministic_compositor).schema_version) ||
    requests.length !== 2 ||
    !stringValue(copyLock.copy_lock_sha256) ||
    identityAssets.length === 0 ||
    slots.length === 0
  )
    return null;
  const omissions = [
    ...new Set(
      arrayValue(copyLock.omitted_source_slots)
        .map(objectValue)
        .map((item) => stringValue(item.source_slot_id))
        .filter(Boolean),
    ),
  ];
  return { plan, copyLock, requests, identityAssets, slots, omissions };
}

function StaticCreativePlanSurface({ review }: { review: StaticPlanReview }) {
  const authority = objectValue(review.plan.authority);
  const safeArea = objectValue(review.plan.safe_area);
  const variation = objectValue(review.plan.variation);
  const factBindings = arrayValue(review.plan.fact_bindings);
  return (
    <div className="static-plan-review">
      <section className="static-plan-review__hero">
        <div>
          <span>Approval boundary</span>
          <h2>{review.requests.length} exact image requests are held</h2>
          <p>
            Approval releases only these two hash-bound requests. The winner
            image remains review evidence and is never attached to the provider
            request.
          </p>
        </div>
        <dl>
          <div>
            <dt>Canvas</dt>
            <dd>
              {numberValue(objectValue(review.plan.canvas).width)} ×{" "}
              {numberValue(objectValue(review.plan.canvas).height)}
            </dd>
          </div>
          <div>
            <dt>Safe area</dt>
            <dd>
              x {numberValue(safeArea.x)} · y {numberValue(safeArea.y)} ·{" "}
              {numberValue(safeArea.width)} × {numberValue(safeArea.height)}
            </dd>
          </div>
          <div>
            <dt>Variation</dt>
            <dd>{stringValue(variation.generated_ad_public_id)}</dd>
          </div>
        </dl>
      </section>
      <section className="static-plan-review__authority">
        <header>
          <div>
            <span>Bound authority</span>
            <h3>Reference, target product, commerce, and copy</h3>
          </div>
          <code>
            {shortHash(stringValue(review.copyLock.copy_lock_sha256))}
          </code>
        </header>
        <dl>
          <div>
            <dt>Reference recipe</dt>
            <dd>
              <code>
                {shortHash(stringValue(authority.reference_recipe_sha256))}
              </code>
            </dd>
          </div>
          <div>
            <dt>Product Kit revision</dt>
            <dd>
              <code>
                {shortHash(stringValue(authority.product_kit_revision_sha256))}
              </code>
            </dd>
          </div>
          <div>
            <dt>Commerce revision</dt>
            <dd>
              <code>
                {shortHash(stringValue(authority.commerce_revision_sha256))}
              </code>
            </dd>
          </div>
          <div>
            <dt>Fact bindings</dt>
            <dd>{factBindings.length || "None in this request set"}</dd>
          </div>
        </dl>
      </section>
      <section>
        <header>
          <div>
            <span>Exact target copy</span>
            <h3>
              {review.slots.length} fact-bound text block
              {review.slots.length === 1 ? "" : "s"}
            </h3>
          </div>
        </header>
        <div className="static-plan-copy">
          {review.slots.map((slot, index) => (
            <article key={`${stringValue(slot.source_slot_id)}-${index}`}>
              <strong>{stringValue(slot.text)}</strong>
              <span>{stringValue(slot.role).replaceAll("_", " ")}</span>
              <code>
                {arrayValue(slot.fact_ids).map(String).join(" · ") ||
                  "Product identifier"}
              </code>
            </article>
          ))}
        </div>
        {review.omissions.length > 0 && (
          <footer className="static-plan-omissions">
            <span>Omitted source blocks</span>
            <p>{review.omissions.join(" · ")}</p>
          </footer>
        )}
      </section>
      <section>
        <header>
          <div>
            <span>Product Kit identity inputs</span>
            <h3>
              {review.identityAssets.length} approved reference asset
              {review.identityAssets.length === 1 ? "" : "s"}
            </h3>
          </div>
        </header>
        <ol className="static-plan-assets">
          {review.identityAssets.map((asset, index) => (
            <li key={`${stringValue(asset.asset_id)}-${index}`}>
              <code>{stringValue(asset.asset_id)}</code>
              <p>
                {arrayValue(asset.visible_features).map(String).join(" ") ||
                  "No visible-feature description was projected."}
              </p>
              <small>{shortHash(stringValue(asset.sha256))}</small>
            </li>
          ))}
        </ol>
      </section>
      <section>
        <header>
          <div>
            <span>Exact provider request set</span>
            <h3>Review both requests before release</h3>
          </div>
          <strong>
            {stringValue(review.plan.workflow_profile).replaceAll("_", " ")}
          </strong>
        </header>
        <div className="static-plan-requests">
          {review.requests.map((request, index) => (
            <details
              key={`${stringValue(request.request_sha256)}-${index}`}
              open
            >
              <summary>
                <span>
                  Candidate {numberValue(request.candidate_number) || index + 1}
                </span>
                <strong>{stringValue(request.provider_profile)}</strong>
                <code>{shortHash(stringValue(request.request_sha256))}</code>
              </summary>
              <dl>
                <div>
                  <dt>Identity inputs</dt>
                  <dd>
                    {arrayValue(request.input_asset_ids)
                      .map(String)
                      .join(" · ")}
                  </dd>
                </div>
                <div>
                  <dt>Aspect ratio</dt>
                  <dd>{stringValue(request.aspect_ratio)}</dd>
                </div>
              </dl>
              <pre>{stringValue(request.prompt)}</pre>
            </details>
          ))}
        </div>
      </section>
    </div>
  );
}

type ScratchVoiceOption = NonNullable<
  ProductionReviewTask["correctionOptions"]
>["voices"][number];

function ScratchCreativeComponentSurface({
  step,
  voices,
  selectedVoiceId,
  selectedStepIds,
  onSelectVoice,

  onToggleStep,
}: {
  step: CandidateStep;
  voices: ScratchVoiceOption[];
  selectedVoiceId: string;
  selectedStepIds: string[];
  onSelectVoice: (voiceId: string, voiceLabel: string, targetComponentId?: string) => void;

  onToggleStep: (stepId: string, checked: boolean) => void;
}) {
  if (step.stepId === "script" && step.content.sourceMode === "supplied" && step.content.interpretation)
    return <ScratchSourceAwareReviewSurface content={step.content} />;
  if (step.stepId === "script")
    return (
      <div className="scratch-component-review scratch-script-component">
        <header>
          <span>Narration</span>
          <h2>Read the exact script</h2>
          <p>The approved wording becomes both narration and primary captions.</p>
        </header>
        <blockquote>{stringValue(step.content.completeScript)}</blockquote>
        <dl className="scratch-component-facts">
          <div><dt>Perspective</dt><dd>{humanLabel(stringValue(step.content.narrationPerson))}</dd></div>
          <div><dt>Main character</dt><dd>Unnamed throughout</dd></div>
        </dl>
      </div>
    );
  if (step.stepId === "protagonist-profile")
    return <ScratchProtagonistSurface content={step.content} />;
  if (step.stepId === "supporting-characters")
    return <ScratchSupportingCharactersSurface content={step.content} />;
  if (step.stepId === "selected-style")
    return <ScratchStyleSurface content={step.content} />;
  if (step.stepId.startsWith("voice-native-") && typeof step.content.speaker_id === 'string')
    return (
      <ScratchNativeDialogueSurface
        speakers={[step.content as NativeDialogueSpeaker]}
      />
    );
  if (step.stepId.startsWith("voice-"))
    return (
      <ScratchVoiceSurface
        currentVoiceId={stringValue(step.content.voice_asset_id, step.stepId.replace(/^voice-/u, ""))}
        category={stringValue(step.content.category)}
        narratorResolution={objectValue(step.content.narrator_resolution)}
        voices={voices}
        selectedVoiceId={selectedVoiceId}
        onSelectVoice={onSelectVoice}
      />
    );
  if (step.stepId === "compact-storyboard")
    return (
      <ScratchStoryboardSurface
        content={step.content}
        selectedStepIds={selectedStepIds}
        onToggleStep={onToggleStep}
      />
    );
  if (step.stepId === "dollar-budget")
    return <ScratchBudgetSurface content={step.content} />;
  if (step.contentKind === "image" && step.artifactUrl)
    return (
      <div className="scratch-component-review scratch-character-sheet-review">
        <header>
          <span>Generated character sheet</span>
          <h2>{humanLabel(stringValue(step.content.characterId, step.title).replace("character-", ""))}</h2>
          <p>Review identity, proportions, expressions, wardrobe, and turnaround consistency.</p>
        </header>
        <img src={step.artifactUrl} alt={step.title} />
      </div>
    );
  return (
    <div className="review-canvas-empty">
      <div>
        <strong>Human review renderer missing</strong>
        <p>This component remains blocked rather than exposing machine JSON as a decision surface.</p>
      </div>
    </div>
  );
}

function ScratchProtagonistSurface({ content }: { content: Record<string, unknown> }) {
  const pronouns = objectValue(content.pronouns);
  const supplied = content.sourceMode === "supplied";

  return (
    <div className="scratch-component-review">
      <header>
        <span>{supplied ? "Source-directed main character" : "Unnamed protagonist"}</span>
        <h2>{stringValue(content.persona, "Targeted customer avatar")}</h2>
        <p>{stringValue(content.targeted_avatar)}</p>
      </header>
      <section>
        <h3>Appearance</h3>
        <p>{stringValue(content.appearance)}</p>
      </section>
      <section>
        <h3>Story arc</h3>
        <p>{stringValue(content.story_arc)}</p>
      </section>
      <dl className="scratch-component-facts">
        <div><dt>Narration</dt><dd>{humanLabel(stringValue(content.narration_person))}</dd></div>
        <div><dt>Pronouns</dt><dd>{[pronouns.subject, pronouns.object, pronouns.possessive_adjective].map(String).filter(Boolean).join(" / ")}</dd></div>
        <div><dt>Proper name</dt><dd>{supplied ? stringValue(content.name, "Not specified in source") : "None"}</dd></div>
      </dl>
      <details>
        <summary>Continuity requirements</summary>
        <ul>{arrayValue(content.continuity).map((item, index) => <li key={index}>{String(item)}</li>)}</ul>
      </details>
    </div>
  );
}

function ScratchSupportingCharactersSurface({ content }: { content: Record<string, unknown> }) {
  const interpretation = objectValue(content.interpretation);
  const suppliedCast = arrayValue(interpretation.cast).map(objectValue);
  const characters = arrayValue(content.characters).map(objectValue);
  const displayCharacters = characters;
  return (
    <div className="scratch-component-review">
      <header>
        <span>Supporting cast</span>
        <h2>{displayCharacters.length} recurring story character{displayCharacters.length === 1 ? "" : "s"}</h2>
        <p>{suppliedCast.length ? "Names come from the supplied interpretation and are linked to exact source spans." : "These named characters support the unnamed protagonist and remain visually consistent."}</p>
      </header>
      <div className="scratch-supporting-list">
        {displayCharacters.map((character, index) => (
          <article key={stringValue(character.character_id, stringValue(character.id, String(index)))}>
            <div><span>{humanLabel(stringValue(character.role, stringValue(character.subject_form)))}</span><h3>{stringValue(character.name, humanLabel(stringValue(character.character_id, stringValue(character.id))))}</h3></div>
            <p>{stringValue(character.description)}</p>
            {arrayValue(character.source_segment_ids).length > 0 && <small>Source spans: {arrayValue(character.source_segment_ids).map(String).join(" · ")}</small>}
            <details><summary>Continuity</summary><ul>{arrayValue(character.continuity).map((item, itemIndex) => <li key={itemIndex}>{String(item)}</li>)}</ul></details>
          </article>
        ))}
      </div>
    </div>
  );
}

function ScratchStyleSurface({ content }: { content: Record<string, unknown> }) {
  return (
    <div className="scratch-component-review scratch-style-review">
      <header>
        <span>Selected approved Style</span>
        <h2>{stringValue(content.renderFamily, "Calibrated Scratch 3D Style")}</h2>
        <p>This visual grammar controls every character sheet, staging image, and generated clip.</p>
      </header>
      <dl className="scratch-component-facts">
        <div><dt>Style</dt><dd>{stringValue(content.stylePublicId, stringValue(content.styleId))}</dd></div>
        <div><dt>Authority hash</dt><dd><code>{shortHash(stringValue(content.styleSha256))}</code></dd></div>
      </dl>
    </div>
  );
}

type NativeDialogueSpeaker = {
  speaker_id: string;
  name: string;

  voice_description?: string;
  line_ids?: string[];

};

function ScratchNativeDialogueSurface({ speakers }: { speakers: NativeDialogueSpeaker[] }) {
  return <div className="scratch-component-review scratch-native-dialogue-review">
    <header><span>Native Omni dialogue</span><h2>Saved speaker directions</h2><p>No voice selection or voice approval is required. Each clip for a speaker uses the same saved directions. Actual voices and lip-sync are reviewed in the final clips.</p></header>
    {speakers.map(speaker => <article key={speaker.speaker_id}>
      <h3>{speaker.name}</h3><p>{speaker.voice_description}</p><small>Source speaker: {speaker.speaker_id} · Lines: {speaker.line_ids?.join(' · ')}</small>
    </article>)}
  </div>;
}

export function ScratchVoiceSurface({ currentVoiceId, category, narratorResolution, voices, selectedVoiceId, onSelectVoice }: { currentVoiceId: string; category: string; narratorResolution: Record<string, unknown>; voices: ScratchVoiceOption[]; selectedVoiceId: string; onSelectVoice: (voiceId: string, voiceLabel: string) => void }) {
  const eligibleVoices = voices.filter((voice) => voice.category === "male" || voice.category === "female");
  const activeVoiceId = selectedVoiceId || currentVoiceId;
  const narratorCategory = stringValue(narratorResolution.category, category);
  const narratorIdentity = stringValue(narratorResolution.character_id, "Unnamed protagonist");
  const narratorSource = stringValue(narratorResolution.source, "planned character");
  const pairingLabel = `${humanLabel(narratorCategory || "unassigned")} narrator · ${humanLabel(narratorIdentity)}`;
  const audioRefs = useRef<Record<string, HTMLAudioElement | null>>({});
  const [playingVoiceId, setPlayingVoiceId] = useState("");
  const [currentTimes, setCurrentTimes] = useState<Record<string, number>>({});
  const [durations, setDurations] = useState<Record<string, number>>(() => Object.fromEntries(eligibleVoices.map((voice) => [voice.id, Number(voice.durationSeconds) || 0])));
  const [previewErrors, setPreviewErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    setDurations((current) => Object.fromEntries(eligibleVoices.map((voice) => [voice.id, current[voice.id] || Number(voice.durationSeconds) || 0])));
  }, [voices]);

  useEffect(() => () => {
    Object.values(audioRefs.current).forEach((audio) => audio?.pause());
  }, []);

  const togglePlayback = async (voice: ScratchVoiceOption) => {
    const audio = audioRefs.current[voice.id];
    if (!audio) return;
    if (!audio.paused) {
      audio.pause();
      setPlayingVoiceId("");
      return;
    }
    Object.entries(audioRefs.current).forEach(([voiceId, other]) => {
      if (voiceId !== voice.id) other?.pause();
    });
    setPreviewErrors((current) => ({ ...current, [voice.id]: "" }));
    try {
      await audio.play();
      setPlayingVoiceId(voice.id);
    } catch {
      setPlayingVoiceId("");
      setPreviewErrors((current) => ({ ...current, [voice.id]: "Could not load this preview. Try again." }));
    }
  };

  const seekPreview = (voiceId: string, time: number) => {
    const audio = audioRefs.current[voiceId];
    if (!audio) return;
    audio.currentTime = time;
    setCurrentTimes((current) => ({ ...current, [voiceId]: time }));
  };
  return (
    <div className="scratch-component-review scratch-voice-review">
      <header>
        <span>Voice audition · {pairingLabel}</span>
        <h2>Choose the voice for this ad</h2>
        <p>Planned narrator: {pairingLabel} ({narratorSource}). Approved male and female voice previews are eligible; choosing one is an explicit user override and leaves the rest of the package untouched.</p>
      </header>
      {eligibleVoices.length ? <fieldset>
        <legend>Available approved male and female voices</legend>
        {eligibleVoices.map((voice) => {
          const current = voice.id === currentVoiceId;
          const selected = voice.id === activeVoiceId;
          const durationSeconds = durations[voice.id] || 0;
          const currentSeconds = Math.min(currentTimes[voice.id] || 0, durationSeconds || Number.MAX_SAFE_INTEGER);
          const playing = playingVoiceId === voice.id;
          return <article className={selected ? "is-selected" : ""} key={voice.id}>
            <label className="scratch-voice-choice">
              <input type="radio" name="scratch-voice-choice" value={voice.id} checked={selected} onChange={() => onSelectVoice(voice.id, voice.label)} />
              <span><strong>{voice.label}</strong><small>{current ? "Current choice" : selected ? "Selected" : "Available"}</small></span>
            </label>
            <div className="scratch-voice-player">
              {voice.previewUrl ? <>
              <audio
                ref={(node) => { audioRefs.current[voice.id] = node; }}
                preload="metadata"
                src={voice.previewUrl}
                onLoadedMetadata={(event) => {
                  const mediaDuration = Number.isFinite(event.currentTarget.duration) ? event.currentTarget.duration : Number(voice.durationSeconds) || 0;
                  setDurations((values) => ({ ...values, [voice.id]: mediaDuration }));
                }}
                onTimeUpdate={(event) => {
                  const mediaTime = event.currentTarget.currentTime;
                  setCurrentTimes((values) => ({ ...values, [voice.id]: mediaTime }));
                }}
                onPause={() => setPlayingVoiceId((value) => value === voice.id ? "" : value)}
                onPlay={() => setPlayingVoiceId(voice.id)}
                onEnded={() => { setPlayingVoiceId(""); setCurrentTimes((values) => ({ ...values, [voice.id]: 0 })); }}
                onError={() => setPreviewErrors((values) => ({ ...values, [voice.id]: "Could not load this preview. Try again." }))}
              />
              <button type="button" className="scratch-voice-play" aria-label={`${playing ? "Pause" : "Play"} ${voice.label}`} onClick={() => void togglePlayback(voice)}>
                {playing ? <Pause size={15} /> : <Play size={15} />}
                <span>{playing ? "Pause" : "Play"}</span>
              </button>
              <input aria-label={`Seek ${voice.label} preview`} type="range" min="0" max={Math.max(durationSeconds, 0.01)} step="0.01" value={currentSeconds} onChange={(event) => seekPreview(voice.id, Number(event.target.value))} />
              <output aria-label={`${voice.label} preview time`}>{formatVoiceTime(currentSeconds)} / {formatVoiceTime(durationSeconds)}</output>
              {previewErrors[voice.id] && <p className="scratch-voice-error" role="alert">{previewErrors[voice.id]}</p>}
              </> : <p className="scratch-voice-unavailable">Preview unavailable for this approved voice.</p>}
            </div>
          </article>;
        })}
      </fieldset> : <div className="review-canvas-empty"><p>No approved voice previews are currently available.</p></div>}
      {selectedVoiceId && selectedVoiceId !== currentVoiceId && <p className="scratch-choice-notice">This voice is now in Pending changes. Submit the batch when every requested edit is ready.</p>}
    </div>
  );
}

function formatVoiceTime(seconds: number) {
  if (!Number.isFinite(seconds) || seconds <= 0) return "0:00";
  const wholeSeconds = Math.floor(seconds);
  return `${Math.floor(wholeSeconds / 60)}:${String(wholeSeconds % 60).padStart(2, "0")}`;
}

export function ScratchSourceAwareReviewSurface({ content }: { content: Record<string, unknown> }) {
  const source = objectValue(content.suppliedSource);
  const interpretation = objectValue(content.interpretation);
  const segments = arrayValue(interpretation.segments).map(objectValue);
  const cast = arrayValue(interpretation.cast).map(objectValue);
  const castById = new Map(cast.map((item) => [stringValue(item.id), item]));
  return <div className="scratch-component-review scratch-source-review">
    <header>
      <span>Supplied script interpretation</span>
      <h2>Review speech and visual direction separately</h2>
      <p>This review preserves the supplied wording and labels interpretation without turning directions or named speakers into narrator copy.</p>
      <small>Narrator: {stringValue(castById.get(stringValue(interpretation.narrator_id))?.name, "Unnamed narrator")}</small>
    </header>
    <section>
      <h3>Exact source excerpt</h3>
      <blockquote>{stringValue(source.text)}</blockquote>
      <small title={stringValue(source.sha256)}>Source hash: {shortHash(stringValue(source.sha256))}</small>
    </section>
    <section>
      <h3>Interpreted segments</h3>
      <div className="scratch-source-segments">{segments.filter(segment => segment.kind !== "separator").map((segment, index) => {
        const speaker = castById.get(stringValue(segment.speaker_id));
        const kind = humanLabel(stringValue(segment.kind));
        return <article key={stringValue(segment.id, String(index))}>
          <span>{kind}</span>
          <strong>{speaker ? stringValue(speaker.name, "Unnamed speaker") : "Source instruction"}</strong>
          <blockquote>{stringValue(segment.text)}</blockquote>
          <small>Source span {stringValue(segment.id)} · characters {String(segment.start)}–{String(segment.end)}</small>
          {stringValue(segment.delivery) && <small>Delivery: {stringValue(segment.delivery)}</small>}
          {numberValue(segment.duration_seconds) > 0 && <small>Intended duration: {numberValue(segment.duration_seconds).toFixed(numberValue(segment.duration_seconds) % 1 ? 1 : 0)} seconds</small>}
          {stringValue(segment.continuity_mode) && <small>Continuity: {humanLabel(stringValue(segment.continuity_mode))}</small>}
          {stringValue(segment.treatment) && stringValue(segment.treatment) !== "generated_action" && <small>Treatment: {humanLabel(stringValue(segment.treatment))}</small>}
        </article>;
      })}</div>
    </section>
    <section>
      <h3>Named supplied cast</h3>
      <div className="scratch-supporting-list">{cast.map((character, index) => <article key={stringValue(character.id, String(index))}>
        <h4>{stringValue(character.name, "Unnamed cast member")}</h4>
        <p>{stringValue(character.description)}</p>
      </article>)}</div>
    </section>
  </div>;
}

function ScratchCueAudio({ content, artifactUrl }: { content: Record<string, unknown>; artifactUrl: string }) {
  const seconds = (value: unknown) => `${numberValue(value).toFixed(2)}s`;
  const medium = stringValue(content.medium) === "music" ? "Music" : "Sound effect";
  return (
    <div className="review-audio review-cue-audio">
      <audio src={artifactUrl} controls />
      <dl className="scratch-cue-grid">
        <div><dt>Scripted sound</dt><dd>{stringValue(content.scriptedSound, "Unnamed cue")}</dd></div>
        <div><dt>Kind</dt><dd>{medium}</dd></div>
        <div><dt>Starts</dt><dd>{seconds(content.startSeconds)}</dd></div>
        <div><dt>Stops</dt><dd>{seconds(content.endSeconds)}</dd></div>
        <div><dt>Mix level under speech</dt><dd>{numberValue(content.mixVolume).toFixed(2)}</dd></div>
        <div><dt>Source span</dt><dd>{stringValue(content.sourceSegmentId, "—")}</dd></div>
        <div><dt>Asset provenance</dt><dd>{humanLabel(stringValue(content.rightsBasis, "unrecorded"))}</dd></div>
        <div><dt>Provider</dt><dd>{stringValue(content.provider, "—")}</dd></div>
        <div><dt>Approved request</dt><dd>{stringValue(content.requestId, "—")}</dd></div>
      </dl>
      <p className="scratch-cue-note">This sound comes from its own approved audio request bound to the exact source span above. It is never taken from generated video audio, and no rights to a named recording are assumed.</p>
    </div>
  );
}

export function ScratchStoryboardSurface({ content, selectedStepIds, onToggleStep }: { content: Record<string, unknown>; selectedStepIds: string[]; onToggleStep: (stepId: string, checked: boolean) => void }) {
  const units = arrayValue(content.units).map(objectValue);
  return (
    <div className="scratch-component-review scratch-storyboard-review">
      <header>
        <span>Compact storyboard</span>
        <h2>Review the complete visual progression</h2>
        <p>All {units.length} story units are shown together so pacing, continuity, action, and motion ownership can be judged as one plan.</p>
      </header>
      <div className="scratch-storyboard-list">
        {units.map((wrapper, index) => {
          const unit = objectValue(wrapper.content);
          const timing = objectValue(objectValue(unit.source_intent).timing);
          const stepId = stringValue(wrapper.stepId);
          return <article key={stepId || index}>
            <div className="scratch-storyboard-index"><b>{String(index + 1).padStart(2, "0")}</b><span>{humanLabel(stringValue(unit.story_function))}</span></div>
            <div className="scratch-storyboard-copy">
              <header><div><h3>{stringValue(unit.visual_purpose)}</h3><p>{stringValue(unit.environment)}</p></div><label><input type="checkbox" checked={selectedStepIds.includes(stepId)} onChange={(event) => onToggleStep(stepId, event.target.checked)} /> Select for targeted edit</label></header>
              <p className="scratch-storyboard-moment">{stringValue(unit.moment)}</p>
              <dl>
                <div><dt>Start frame</dt><dd>{stringValue(unit.start_frame)}</dd></div>
                <div><dt>Primary action</dt><dd>{stringValue(unit.primary_action)}</dd></div>
                <div><dt>Opening</dt><dd>{stringValue(unit.opening_state)}</dd></div>
                <div><dt>Ending</dt><dd>{stringValue(unit.ending_state)}</dd></div>
                <div><dt>Camera</dt><dd>{stringValue(unit.camera_behavior)}</dd></div>
                {stringValue(timing.mode) && stringValue(timing.mode) !== "natural" && <div><dt>Timing authority</dt><dd>{humanLabel(stringValue(timing.mode))} · {numberValue(timing.intended_duration_seconds).toFixed(numberValue(timing.intended_duration_seconds) % 1 ? 1 : 0)} seconds</dd></div>}
                {stringValue(timing.treatment) && stringValue(timing.treatment) !== "generated_action" && <div><dt>Source treatment</dt><dd>{humanLabel(stringValue(timing.treatment))}</dd></div>}
                {Object.entries(objectValue(unit.spatial_staging)).filter(([key]) => key !== "required_visible_parts").map(([key, value]) => <div key={key}><dt>{humanLabel(key)}</dt><dd>{key === "action_view" ? humanLabel(stringValue(value)) : stringValue(value)}</dd></div>)}
                {arrayValue(objectValue(unit.spatial_staging).required_visible_parts).length > 0 && <div><dt>Visible features</dt><dd>{arrayValue(objectValue(unit.spatial_staging).required_visible_parts).map((part) => humanLabel(String(part))).join(" · ")}</dd></div>}
                <div><dt>Allowed secondary motion</dt><dd>{arrayValue(unit.permitted_secondary_motion).map(String).join(" · ") || "None"}</dd></div>
              </dl>
              <p className="scratch-static-lock">Everything else remains world-fixed.</p>
            </div>
          </article>;
        })}
      </div>
    </div>
  );
}

function ScratchBudgetSurface({ content }: { content: Record<string, unknown> }) {
  return (
    <div className="scratch-component-review scratch-budget-review">
      <header>
        <span>Cost tracking</span>
        <h2>Production cost estimates</h2>
        <p>Costs are observational, not a spending ceiling. Recovery remains limited to its authorized requests.</p>
      </header>
      <dl className="scratch-budget-grid">
        <div><dt>Pre-review estimate</dt><dd>${numberValue(content.maximumPreReviewSpendUsd).toFixed(2)}</dd></div>
        <div><dt>Planned production</dt><dd>${numberValue(content.estimatedPlannedSpendUsd).toFixed(2)}</dd></div>
        <div><dt>Recovery estimate</dt><dd>${numberValue(content.recoveryReserveUsd).toFixed(2)}</dd></div>
        <div><dt>Initial total estimate</dt><dd>${numberValue(content.hardMaximumUsd ?? (numberValue(content.estimatedPlannedSpendUsd) + numberValue(content.recoveryReserveUsd))).toFixed(2)}</dd></div>
      </dl>
      <details><summary>Diagnostic call estimate</summary><p>{numberValue(content.expectedCalls)} expected paid calls. This count is bookkeeping, not a separate approval criterion.</p></details>
    </div>
  );
}

function ScratchCreativePlanSurface({
  content,
}: {
  content: Record<string, unknown>;
}) {
  const angle = objectValue(content.selectedAngle);
  const route = objectValue(content.formatRoute);
  const style = objectValue(content.style);
  const world = objectValue(content.world);
  const storyUnits = arrayValue(content.storyUnits);
  const characters = arrayValue(world.characters);
  const environments = arrayValue(world.environments);
  const scriptLines = arrayValue(content.scriptLines);
  const costs = objectValue(content.costEstimate);
  const originality = objectValue(content.originalityReport);
  const archetype = objectValue(content.creativeArchetype);
  const archetypeAlternatives = arrayValue(content.alternativeArchetypes);
  const exemplarLineage = arrayValue(content.exemplarLineage);
  const characterRequests = objectValue(content.characterRequestSet);
  const audioRequests = objectValue(content.audioRequestSet);
  const linesById = new Map(
    scriptLines.map((item) => {
      const line = objectValue(item);
      return [stringValue(line.line_id), line];
    }),
  );
  const charactersById = new Map(
    characters.map((item) => {
      const character = objectValue(item);
      return [stringValue(character.character_id), character];
    }),
  );
  return (
    <div className="scratch-plan-review">
      <section className="scratch-plan-hero">
        <span>Decision required</span>
        <h2>Approve this story and its complete cast?</h2>
        <p>
          {stringValue(angle.angle_proposition, "Approved Product Kit angle")}
        </p>
        <div>
          <i>{humanLabel(stringValue(route.subformat))}</i>
          <i>{humanLabel(stringValue(route.audio_mode))}</i>
          <i>{stringValue(style.renderFamily)}</i>
        </div>
        <aside>
          Approval releases exactly{" "}
          {numberValue(characterRequests.request_count)} character-sheet
          requests. Narration, staging, the deterministic mock, and video
          generation remain held.
        </aside>
      </section>
      <section className="scratch-casting-review">
        <header>
          <div>
            <span>Casting to approve</span>
            <h3>
              {characters.length} specific recurring character
              {characters.length === 1 ? "" : "s"}
            </h3>
          </div>
          <strong>Gender locked before generation</strong>
        </header>
        <div className="scratch-cast-list">
          {characters.map((item, index) => {
            const character = objectValue(item);
            const casting = objectValue(character.casting_authority);
            const continuity = objectValue(character.continuity);
            return (
              <article key={stringValue(character.character_id, String(index))}>
                <div className="scratch-cast-heading">
                  <b>{index + 1}</b>
                  <div>
                    <h4>
                      {humanLabel(
                        stringValue(character.character_id).replace(
                          "character-",
                          "",
                        ),
                      )}
                    </h4>
                    <p>{stringValue(character.role)}</p>
                  </div>
                  <em>
                    {humanLabel(
                      stringValue(character.gender, "Gender missing"),
                    )}
                  </em>
                </div>
                <dl>
                  <div>
                    <dt>Age</dt>
                    <dd>{stringValue(continuity.age)}</dd>
                  </div>
                  <div>
                    <dt>Appearance</dt>
                    <dd>{stringValue(character.description)}</dd>
                  </div>
                  <div>
                    <dt>Wardrobe</dt>
                    <dd>{stringValue(continuity.wardrobe)}</dd>
                  </div>
                  <div>
                    <dt>Why this person</dt>
                    <dd>{stringValue(casting.audience_alignment)}</dd>
                  </div>
                  <div>
                    <dt>Story job</dt>
                    <dd>{stringValue(casting.story_alignment)}</dd>
                  </div>
                </dl>
              </article>
            );
          })}
        </div>
      </section>
      <section className="scratch-story-review">
        <header>
          <div>
            <span>How the ad plays</span>
            <h3>{storyUnits.length} causal story beats</h3>
          </div>
          <strong>{environments.length} environments</strong>
        </header>
        <div className="scratch-beat-list">
          {storyUnits.map((item, index) => {
            const unit = objectValue(item);
            const narration = arrayValue(unit.line_ids)
              .map((lineId) => stringValue(linesById.get(String(lineId))?.text))
              .filter(Boolean)
              .join(" ");
            const cast = arrayValue(unit.character_ids).map((characterId) => {
              const character = charactersById.get(String(characterId));
              return character
                ? `${humanLabel(String(characterId).replace("character-", ""))} (${humanLabel(stringValue(character.gender))})`
                : String(characterId);
            });
            return (
              <article key={stringValue(unit.unit_id, String(index))}>
                <div className="scratch-beat-index">
                  <b>{String(index + 1).padStart(2, "0")}</b>
                  <span>{humanLabel(stringValue(unit.story_function))}</span>
                </div>
                <div>
                  <blockquote>{narration}</blockquote>
                  <h4>{stringValue(unit.subject)}</h4>
                  <p>{stringValue(unit.primary_action)}</p>
                  <dl>
                    <div>
                      <dt>Starts</dt>
                      <dd>{stringValue(unit.opening_state)}</dd>
                    </div>
                    <div>
                      <dt>Ends</dt>
                      <dd>{stringValue(unit.ending_state)}</dd>
                    </div>
                    <div>
                      <dt>Cast</dt>
                      <dd>{cast.join(" · ") || "No character visible"}</dd>
                    </div>
                    <div>
                      <dt>Product</dt>
                      <dd>
                        {unit.product_visible === true
                          ? "Visible using the exact approved Product Kit references"
                          : "Not visible"}
                      </dd>
                    </div>
                  </dl>
                </div>
              </article>
            );
          })}
        </div>
      </section>
      <section className="scratch-script-review">
        <details>
          <summary>
            <span>Approved narration</span>
            <strong>Read the full locked script</strong>
          </summary>
          <blockquote>{stringValue(content.script)}</blockquote>
          <small>
            {stringValue(originality.status, "unverified")} · exact voice timing
            is compiled only after the cast is approved
          </small>
        </details>
      </section>
      <section className="scratch-plan-boundary">
        <header>
          <div>
            <span>What this decision does</span>
            <h3>Character sheets only</h3>
          </div>
          <strong>${numberValue(costs.known_usd).toFixed(2)} known cost</strong>
        </header>
        <div>
          <p>
            <b>Released now:</b> one complete batch of{" "}
            {numberValue(characterRequests.request_count)} character sheets for
            the cast above.
          </p>
          <p>
            <b>Still held:</b> {numberValue(audioRequests.request_count)}{" "}
            narration request, exact word timing, quick cuts, staging images,
            deterministic mock, and every video request.
          </p>
        </div>
        <details>
          <summary>Production lineage and construction authority</summary>
          <p>
            {stringValue(
              archetype.label,
              humanLabel(stringValue(route.creative_archetype_id)),
            )}
          </p>
          <p>
            Retrieved exemplars:{" "}
            {exemplarLineage
              .map((item) => stringValue(item))
              .filter(Boolean)
              .join(" · ") || "None"}
          </p>
          <p>
            Alternative constructions considered:{" "}
            {archetypeAlternatives
              .map((item) => stringValue(objectValue(item).label))
              .filter(Boolean)
              .join(" · ") || "None compatible"}
          </p>
          <code>
            {shortHash(stringValue(characterRequests.request_set_sha256))}
          </code>
        </details>
      </section>
    </div>
  );
}

function objectValue(value: unknown): Record<string, unknown> {
  return value && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}
function arrayValue(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}
function stringValue(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback;
}
function numberValue(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) ? value : 0;
}
function humanLabel(value: string): string {
  return value
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
function shortHash(value: string): string {
  return value.length === 64
    ? `${value.slice(0, 12)}…${value.slice(-8)}`
    : value || "Not projected";
}

function gateLabel(value: ProductionReviewTask["gate"]): string {
  return {
    consolidated_creative_plan: "Creative plan",
    image_batch_review: "Image batch",
    complete_draft_review: "Complete draft",
    video_batch_review: "Video batch",
    final_acceptance: "Final approval",
    static_creative_plan: "Static creative plan",
    static_candidate_batch_review: "Static candidate batch",
    static_final_batch_acceptance: "Static final acceptance",
    scratch_creative_plan: "Scratch story and casting brief",
    scratch_staging_draft:
      "Scratch complete quick-cut mock and video authorization",
    scratch_final_acceptance: "Scratch final approval",
  }[value];
}
function stableJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(stableJson).join(",")}]`;
  if (value && typeof value === "object")
    return `{${Object.entries(value as Record<string, unknown>)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([key, child]) => `${JSON.stringify(key)}:${stableJson(child)}`)
      .join(",")}}`;
  return JSON.stringify(value) ?? "null";
}
async function browserSha256(value: unknown): Promise<string> {
  const bytes = new TextEncoder().encode(stableJson(value));
  const digest = await crypto.subtle.digest("SHA-256", bytes);
  return [...new Uint8Array(digest)]
    .map((item) => item.toString(16).padStart(2, "0"))
    .join("");
}
